import React, { useEffect, useState, useRef } from "react";
import { api, BACKEND_URL } from "../lib/api";
import { toast } from "sonner";
import PhoneField from "../components/PhoneField";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { PageHeader, KpiCard } from "../components/Shared";
import Money from "../components/Money";
import { fmtFCFA, fmtDate, statusBadgeClass, ROLES } from "../lib/format";
import {
  Briefcase, TrendingUp, Receipt, Scale, Users, AlertTriangle, Clock, CheckCircle2, UserPlus, FileCheck, Mail,
  Eye, EyeOff, ChevronDown, ChevronsUpDown, FileSignature, Megaphone, UserCircle, Pencil, FolderKanban, Camera, Circle, ListTodo, CalendarDays, Handshake, Sun,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, Legend, BarChart, Bar,
} from "recharts";
import { Link, useNavigate } from "react-router-dom";
import SupervisorDashboard from "./SupervisorDashboard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const PIE_COLORS = ["#1E3A8A", "#0284C7", "#059669", "#F59E0B", "#DC2626", "#7C3AED", "#64748B"];
const TOOLTIP_STYLE = { contentStyle: { borderRadius: 10, border: "1px solid #e2e8f0", boxShadow: "0 10px 30px -14px rgba(15,23,42,.30)", fontSize: 12, padding: "8px 12px" }, cursor: { fill: "rgba(30,58,138,0.05)" } };
const SECTION_IDS = ["income-exp", "projects-category", "income-by-source", "income-source-trend", "payments-by-purpose", "expenditures-by-purpose"];
const WP_STATUS_STYLE = { planned: "bg-slate-100 text-slate-700 border-slate-200", in_progress: "bg-blue-100 text-blue-700 border-blue-200", done: "bg-emerald-100 text-emerald-700 border-emerald-200" };
const WP_STATUS_LABEL = { planned: "Planned", in_progress: "In progress", done: "Done" };
const WP_PRIORITY_BAR = { High: "border-l-red-500", Medium: "border-l-amber-500", Low: "border-l-slate-300" };
const todayStr = () => new Date().toISOString().slice(0, 10);

export default function Dashboard() {
  const { user } = useAuth();
  if (user.role === ROLES.SS) return <SupervisorDashboard />;
  return <MainDashboard />;
}

function MainDashboard() {
  const { user } = useAuth();
  const [drill, setDrill] = useState(null);
  const [nowTs, setNowTs] = useState(Date.now());
  const { data: stats = null } = useQuery({ queryKey: ["dashboard-stats"], queryFn: () => api.get("/dashboard/stats").then((r) => r.data) });
  const { data: charts = null } = useQuery({ queryKey: ["dashboard-charts"], queryFn: () => api.get("/dashboard/charts").then((r) => r.data) });
  const { data: recent = null } = useQuery({ queryKey: ["dashboard-recent"], queryFn: () => api.get("/dashboard/recent").then((r) => r.data) });
  const { data: checkStats = null } = useQuery({ queryKey: ["checks-summary"], queryFn: () => api.get("/checks/summary").then((r) => r.data), enabled: [ROLES.CEO, ROLES.FM].includes(user.role) });
  const { data: allRequests = [] } = useQuery({ queryKey: ["requests"], queryFn: () => api.get("/requests").then((r) => r.data) });
  const canQuotes = [ROLES.CEO, ROLES.ACEO, ROLES.GM].includes(user.role);
  const canSolar = [ROLES.CEO, ROLES.GM, ROLES.ACEO, ROLES.FM].includes(user.role);
  const { data: solarSizings = [] } = useQuery({ queryKey: ["solar-sizings"], queryFn: () => api.get("/solar-sizings").then((r) => r.data), enabled: canSolar });
  const { data: quoteStats = null } = useQuery({ queryKey: ["quotation-stats"], queryFn: () => api.get("/quotations/stats").then((r) => r.data), enabled: canQuotes });
  const seeAnnouncements = [ROLES.EMPLOYEE, ROLES.INTERN].includes(user.role);
  const isStaff = seeAnnouncements;
  const { data: announcements = [] } = useQuery({ queryKey: ["announcements"], queryFn: () => api.get("/announcements").then((r) => r.data), enabled: seeAnnouncements });
  const { data: profile = null } = useQuery({ queryKey: ["employee-me"], queryFn: () => api.get("/employees/me").then((r) => r.data), enabled: isStaff });
  const { data: myProjects = [] } = useQuery({ queryKey: ["my-projects-dash"], queryFn: () => api.get("/projects/mine").then((r) => r.data), enabled: isStaff });
  const qc = useQueryClient();
  const navigate = useNavigate();
  const isIntern = user.role === ROLES.INTERN;
  const { data: myInternships = [] } = useQuery({ queryKey: ["my-internships"], queryFn: () => api.get("/internship-forms").then((r) => r.data), enabled: isIntern });
  const hasInternship = myInternships.length > 0;
  const [internOpen, setInternOpen] = useState(false);
  const [internForm, setInternForm] = useState({ student_name: user.name || "", school: "", period_start: "", period_end: "" });
  const submitIntern = async () => {
    const f = internForm;
    if (!f.student_name.trim() || !f.school.trim() || !f.period_start || !f.period_end) { toast.error("Please complete all fields."); return; }
    try {
      await api.post("/internship-forms", f);
      toast.success("Student form requested — sent to the CEO for approval.");
      setInternOpen(false);
      qc.invalidateQueries({ queryKey: ["my-internships"] });
      navigate("/students");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit request."); }
  };
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [phoneVal, setPhoneVal] = useState("");
  const [phoneErr, setPhoneErr] = useState(false);
  const openPhone = () => { setPhoneVal(profile?.phone || user.phone || ""); setPhoneErr(false); setPhoneOpen(true); };
  const savePhone = async () => {
    if (phoneVal && !/^\+[1-9]\d{6,14}$/.test(phoneVal)) { setPhoneErr(true); return; }
    try {
      await api.post("/employees/me/phone", { phone: phoneVal });
      await qc.invalidateQueries({ queryKey: ["employee-me"] });
      toast.success("Phone updated.");
      setPhoneOpen(false);
    } catch (e) { toast.error("Could not update phone."); }
  };
  const { data: myTasks = [] } = useQuery({ queryKey: ["my-tasks"], queryFn: () => api.get("/tasks/mine").then((r) => r.data), enabled: isStaff });
  const { data: mySchedule = [] } = useQuery({ queryKey: ["my-schedule"], queryFn: () => api.get("/work-program/mine").then((r) => r.data), enabled: isStaff });
  const { data: myContracts = [] } = useQuery({ queryKey: ["my-contracts"], queryFn: () => api.get("/salaries/mine").then((r) => r.data), enabled: isStaff && !!profile?.is_subcontractor });
  const photoInputRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoUrl = profile?.has_photo ? `${BACKEND_URL}/api/employees/me/photo?v=${encodeURIComponent(profile.photo_updated_at || "")}` : null;
  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post("/employees/me/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await qc.invalidateQueries({ queryKey: ["employee-me"] });
      toast.success("Photo updated.");
    } catch (err) { toast.error("Could not upload photo."); }
    finally { setUploadingPhoto(false); if (photoInputRef.current) photoInputRef.current.value = ""; }
  };
  const toggleTask = async (t) => {
    try {
      await api.patch(`/tasks/${t.id}`, { status: t.status === "done" ? "open" : "done" });
      await qc.invalidateQueries({ queryKey: ["my-tasks"] });
    } catch { toast.error("Could not update task."); }
  };
  useEffect(() => { if (isStaff) api.post("/announcements/mark-read").catch(() => {}); }, [isStaff]);
  const pendingRecon = allRequests.filter((r) => r.status === "executed" && r.purpose === "Purchases" && r.created_by === user?.id && r.reconcile_due_at);
  const { data: subContractsDash = [] } = useQuery({ queryKey: ["sub-contracts"], queryFn: () => api.get("/sub-contracts").then((r) => r.data), enabled: user.role === ROLES.CEO });
  const pendingSubContracts = subContractsDash.filter((c) => c.status === "pending");
  const canToggle = user.role === ROLES.CEO;
  const [showDash, setShowDash] = useState(() => user.role !== ROLES.CEO || localStorage.getItem("stems_dash_visible") !== "0");
  const toggleDash = () => setShowDash((v) => { localStorage.setItem("stems_dash_visible", v ? "0" : "1"); return !v; });
  const [sections, setSections] = useState(() => Object.fromEntries(SECTION_IDS.map((id) => [id, localStorage.getItem(`stems_sec_${id}`) !== "0"])));
  const setSection = (id, open) => setSections((s) => { localStorage.setItem(`stems_sec_${id}`, open ? "1" : "0"); return { ...s, [id]: open }; });
  const setAllSections = (open) => setSections(() => { const m = {}; SECTION_IDS.forEach((id) => { localStorage.setItem(`stems_sec_${id}`, open ? "1" : "0"); m[id] = open; }); return m; });
  const allCollapsed = SECTION_IDS.every((id) => !sections[id]);

  const openDrill = (month) => {
    if (!month) return;
    api.get(`/dashboard/month/${month}`).then(({ data }) => setDrill({ month, data }));
  };
  useEffect(() => { const t = setInterval(() => setNowTs(Date.now()), 1000); return () => clearInterval(t); }, []);
  const fmtCountdown = (dueIso) => {
    const ms = new Date(dueIso).getTime() - nowTs;
    if (ms <= 0) return { text: "Overdue — salary debit imminent", danger: true };
    const h = Math.floor(ms / 3600000), m = Math.floor((ms % 3600000) / 60000), s = Math.floor((ms % 60000) / 1000);
    return { text: `${h}h ${m}m ${s}s left`, danger: ms < 2 * 3600000 };
  };

  return (
    <div>
      <PageHeader title={`Welcome, ${user.name.split(" ")[0]}`}
        subtitle={isStaff ? "Your workspace overview" : "Business performance overview · all amounts in FCFA (XAF)"}>
        {canToggle && (
          <div className="flex items-center gap-2">
          {showDash && (
            <motion.button
              data-testid="collapse-all-btn"
              onClick={() => setAllSections(allCollapsed)}
              whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 400, damping: 22 }}
              className="inline-flex items-center gap-2 h-9 px-3.5 rounded-lg border border-slate-200 bg-white text-slate-600 text-sm font-medium hover:bg-slate-50 transition-colors">
              <ChevronsUpDown className="h-4 w-4" />
              {allCollapsed ? "Expand all" : "Collapse all"}
            </motion.button>
          )}
          <motion.button
            data-testid="toggle-dashboard-btn"
            onClick={toggleDash}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.95 }}
            transition={{ type: "spring", stiffness: 400, damping: 22 }}
            className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-[#1E3A8A] text-white text-sm font-medium shadow-sm hover:bg-[#152C6F] transition-colors"
            aria-pressed={!showDash}>
            <span className="relative flex h-4 w-4 items-center justify-center">
              <AnimatePresence mode="wait" initial={false}>
                <motion.span key={showDash ? "hide" : "show"}
                  initial={{ opacity: 0, rotate: -45, scale: 0.5 }}
                  animate={{ opacity: 1, rotate: 0, scale: 1 }}
                  exit={{ opacity: 0, rotate: 45, scale: 0.5 }}
                  transition={{ duration: 0.18 }}
                  className="absolute inset-0 flex items-center justify-center">
                  {showDash ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </motion.span>
              </AnimatePresence>
            </span>
            {showDash ? "Hide dashboard" : "Show dashboard"}
          </motion.button>
          </div>
        )}
      </PageHeader>

      {isIntern && (
        <div className="mb-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm flex items-center justify-between gap-4" data-testid="intern-internship-card">
          <div>
            <h3 className="font-heading font-semibold text-slate-800">Student Form</h3>
            <p className="text-sm text-slate-500">Request your student report form and record your daily activities.</p>
          </div>
          {hasInternship
            ? <Button data-testid="intern-open-form" onClick={() => navigate("/students")} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]">Open my form</Button>
            : <Button data-testid="intern-request-form" onClick={() => setInternOpen(true)} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]">Request Student Form</Button>}
        </div>
      )}

      <Dialog open={internOpen} onOpenChange={setInternOpen}>
        <DialogContent data-testid="intern-request-dialog">
          <DialogHeader><DialogTitle>Request Student Form</DialogTitle>
            <DialogDescription>Fill in your details. The form will be sent to the CEO for approval.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label className="text-sm">Student name</Label>
              <Input data-testid="intern-student-name" value={internForm.student_name} onChange={(e) => setInternForm({ ...internForm, student_name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label className="text-sm">University / School</Label>
              <Input data-testid="intern-school" value={internForm.school} onChange={(e) => setInternForm({ ...internForm, school: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-sm">Period start</Label>
                <Input type="date" data-testid="intern-period-start" value={internForm.period_start} onChange={(e) => setInternForm({ ...internForm, period_start: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="text-sm">Period end</Label>
                <Input type="date" data-testid="intern-period-end" value={internForm.period_end} onChange={(e) => setInternForm({ ...internForm, period_end: e.target.value })} /></div>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setInternOpen(false)}>Cancel</Button>
            <Button data-testid="intern-submit-request" onClick={submitIntern} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]">Submit</Button>
          </div>
        </DialogContent>
      </Dialog>

      {seeAnnouncements && (
        <div className="mb-6" data-testid="dash-announcements">
          <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-700"><Megaphone className="h-4 w-4" /> Announcements</div>
          {announcements.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-400" data-testid="announcements-empty">No announcements right now.</div>
          ) : (
            <div className="space-y-3">
              {announcements.map((a) => (
                <div key={a.id} data-testid={`announcement-${a.id}`} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="font-heading font-semibold text-slate-800">{a.title}</div>
                  <div className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{a.message}</div>
                  <div className="text-xs text-slate-400 mt-2">{fmtDate(a.created_at)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {isStaff && (
        <div className="mb-6" data-testid="dash-employee-profile">
          <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-700"><UserCircle className="h-4 w-4" /> My Profile</div>
          <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-start justify-between gap-4 mb-6">
              <div className="flex items-center gap-4 min-w-0">
                <div className="relative h-14 w-14 shrink-0">
                  {photoUrl ? (
                    <img src={photoUrl} alt="" className="h-14 w-14 rounded-full object-cover border border-slate-200" data-testid="profile-avatar-img" />
                  ) : (
                    <div className="h-14 w-14 rounded-full bg-[#1E3A8A] text-white flex items-center justify-center text-lg font-bold" data-testid="profile-avatar">
                      {(profile?.name || user.name || "?").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                    </div>
                  )}
                  <button type="button" onClick={() => photoInputRef.current?.click()} disabled={uploadingPhoto}
                    className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full bg-white border border-slate-200 shadow flex items-center justify-center text-slate-500 hover:text-[#1E3A8A] transition-colors" data-testid="profile-photo-btn" title="Change photo">
                    <Camera className="h-3.5 w-3.5" />
                  </button>
                  <input ref={photoInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={uploadPhoto} data-testid="profile-photo-input" />
                </div>
                <div className="min-w-0">
                  <div className="font-heading font-semibold text-slate-800 text-lg truncate" data-testid="profile-name">{profile?.name || user.name}</div>
                  <div className="text-sm text-slate-500 truncate">{profile?.designation || profile?.role || user.role}</div>
                </div>
              </div>
              <button data-testid="edit-phone-btn" onClick={openPhone}
                className="shrink-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 transition-colors">
                <Pencil className="h-3.5 w-3.5" /> Edit phone
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
              <ProfileField label="Role" value={profile?.role || user.role} />
              <ProfileField label="Designation" value={profile?.designation} />
              <ProfileField label="Email" value={profile?.email || user.email} />
              <ProfileField label="Phone" value={profile?.phone || user.phone} />
              <ProfileField label="Staff Category" value={profile?.staff_category} />
              {profile?.subcontractor_name && <ProfileField label="Subcontractor" value={profile.subcontractor_name} />}
              <ProfileField label="Business Unit(s)" value={(profile?.units || []).join(", ")} />
              <ProfileField label="Date Added" value={profile?.date_joined ? fmtDate(profile.date_joined) : ""} />
            </div>
          </div>
        </div>
      )}

      {isStaff && (
        <div className="mb-6" data-testid="dash-my-projects">
          <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-700"><FolderKanban className="h-4 w-4" /> My Projects</div>
          <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            {myProjects.length === 0 ? (
              <div className="p-6 text-sm text-slate-400" data-testid="my-projects-empty">You are not assigned to any projects yet.</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {myProjects.map((p) => (
                  <Link to={`/projects/${p.id}`} key={p.id} data-testid={`dash-my-project-${p.id}`} className="flex items-center justify-between px-5 py-3 hover:bg-slate-50">
                    <div className="min-w-0">
                      <div className="font-medium text-slate-800 truncate">{p.name}</div>
                      <div className="text-xs text-slate-400">{p.category || "—"}{p.end_date ? ` · due ${fmtDate(p.end_date)}` : ""}</div>
                    </div>
                    <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-semibold border ${statusBadgeClass(p.status)}`}>{p.status}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {isStaff && (
        <div className="mb-6" data-testid="dash-my-schedule">
          <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-700"><CalendarDays className="h-4 w-4" /> My Schedule</div>
          <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            {mySchedule.length === 0 ? (
              <div className="p-6 text-sm text-slate-400" data-testid="my-schedule-empty">No activities scheduled for you yet.</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {mySchedule.map((w) => {
                  const overdue = w.status !== "done" && (w.end_date || w.start_date) < todayStr();
                  return (
                    <div key={w.id} data-testid={`dash-schedule-${w.id}`} className={`flex items-start justify-between gap-3 px-5 py-3 border-l-4 ${WP_PRIORITY_BAR[w.priority] || "border-l-slate-200"}`}>
                      <div className="min-w-0">
                        <div className={`text-sm font-medium truncate ${w.status === "done" ? "text-slate-400 line-through" : "text-slate-800"}`}>{w.title}</div>
                        <div className="text-xs text-slate-400 mt-0.5">
                          {fmtDate(w.start_date)}{w.end_date && w.end_date !== w.start_date ? ` → ${fmtDate(w.end_date)}` : ""}
                          {w.project_name ? ` · ${w.project_name}` : ""}
                        </div>
                        {overdue && <div className="text-xs font-semibold text-red-600 mt-0.5" data-testid={`schedule-overdue-${w.id}`}>Overdue</div>}
                      </div>
                      <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-semibold border ${WP_STATUS_STYLE[w.status] || ""}`}>{WP_STATUS_LABEL[w.status] || w.status}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {isStaff && profile?.is_subcontractor && (
        <div className="mb-6" data-testid="dash-my-contracts">
          <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-700"><Handshake className="h-4 w-4" /> My Contracts</div>
          <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            {myContracts.length === 0 ? (
              <div className="p-6 text-sm text-slate-400" data-testid="my-contracts-empty">No contracts recorded yet.</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {myContracts.map((c) => (
                  <div key={c.id} data-testid={`dash-contract-${c.id}`} className="px-5 py-3">
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <div className="text-sm font-medium text-slate-800">{c.payment_schedule && c.payment_schedule !== "Undefined" ? c.payment_schedule : "Contract"}</div>
                      <div className="text-xs text-slate-400">{fmtDate(c.created_at)}</div>
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div><div className="text-xs text-slate-400">Contract</div><div className="text-sm font-semibold text-purple-700">{fmtFCFA(c.contract_amount || 0)}</div></div>
                      <div><div className="text-xs text-slate-400">Advance</div><div className="text-sm font-semibold text-amber-600">{fmtFCFA(c.advance || 0)}</div></div>
                      <div><div className="text-xs text-slate-400">Balance</div><div className="text-sm font-semibold text-emerald-600">{fmtFCFA((c.contract_amount || 0) - (c.advance || 0))}</div></div>
                    </div>
                    {(c.contract_amount || 0) > 0 && (() => {
                      const pct = Math.max(0, Math.min(100, Math.round(((c.advance || 0) / (c.contract_amount || 1)) * 100)));
                      return (
                        <div className="mt-3" data-testid={`contract-progress-${c.id}`}>
                          <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
                            <span>Advance paid</span><span className="font-mono-num text-slate-600" data-testid={`contract-progress-pct-${c.id}`}>{pct}%</span>
                          </div>
                          <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                            <div className="h-full rounded-full bg-amber-500 transition-all" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {isStaff && (
        <div className="mb-6" data-testid="dash-my-tasks">
          <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-700"><ListTodo className="h-4 w-4" /> My Tasks</div>
          <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            {myTasks.length === 0 ? (
              <div className="p-6 text-sm text-slate-400" data-testid="my-tasks-empty">No tasks assigned to you.</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {myTasks.map((t) => (
                  <div key={t.id} data-testid={`dash-task-${t.id}`} className="flex items-center gap-3 px-5 py-3">
                    <button onClick={() => toggleTask(t)} data-testid={`task-toggle-${t.id}`} className="shrink-0 text-slate-400 hover:text-emerald-600 transition-colors">
                      {t.status === "done" ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <Circle className="h-5 w-5" />}
                    </button>
                    <div className="min-w-0 flex-1">
                      <div className={`text-sm font-medium truncate ${t.status === "done" ? "text-slate-400 line-through" : "text-slate-800"}`}>{t.title}</div>
                      {t.due_date && (() => {
                        const overdue = t.status !== "done" && t.due_date.slice(0, 10) < todayStr();
                        return <div className={`text-xs mt-0.5 ${overdue ? "font-semibold text-red-600" : "text-slate-400"}`} data-testid={`task-due-${t.id}`}>Due {fmtDate(t.due_date)}{overdue ? " · Overdue" : ""}</div>;
                      })()}
                    </div>
                    {t.status === "done" && <span className="shrink-0 text-xs font-semibold text-emerald-700">Done</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <AnimatePresence initial={false}>
        {!showDash && (
          <motion.div
            key="dashboard-hidden-note"
            data-testid="dashboard-hidden-note"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
            className="mt-2 rounded-2xl border border-dashed border-slate-200 bg-slate-50/70 p-12 text-center">
            <EyeOff className="h-7 w-7 text-slate-300 mx-auto mb-3" />
            <div className="text-sm text-slate-500">Dashboard is hidden. Click <span className="font-medium text-slate-700">“Show dashboard”</span> to bring it back.</div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence initial={false}>
        {showDash && (
          <motion.div
            key="dashboard-body"
            data-testid="dashboard-content"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            style={{ overflow: "hidden" }}>
            <div className="pt-1">

      {pendingRecon.length > 0 && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-2xl p-4 shadow-premium" data-testid="dash-pending-reconciliation">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-amber-800"><Clock className="h-5 w-5" /><h2 className="text-sm font-semibold">Purchases pending reconciliation</h2></div>
            <Link to="/requests" className="text-xs font-semibold text-[#1E3A8A] hover:underline">Open requests →</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {pendingRecon.map((r) => {
              const c = fmtCountdown(r.reconcile_due_at);
              return (
                <Link to="/requests" key={r.id} data-testid={`dash-pending-recon-${r.id}`} className="bg-white rounded-xl border border-amber-100 px-3 py-2 hover:border-amber-300 transition-colors">
                  <div className="text-sm font-medium text-slate-800">{r.request_number} · {fmtFCFA(r.total)}</div>
                  <div className={`text-xs flex items-center gap-1 mt-0.5 ${c.danger ? "text-red-600 font-semibold" : "text-amber-700"}`} data-testid={`dash-countdown-${r.id}`}>{c.danger && <AlertTriangle className="h-3.5 w-3.5" />}{c.text}</div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {user.role === ROLES.CEO && pendingSubContracts.length > 0 && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-2xl p-4 shadow-premium" data-testid="dash-pending-subcontracts">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-blue-800">
              <FileSignature className="h-5 w-5" />
              <h2 className="text-sm font-semibold">{pendingSubContracts.length} sub-contract{pendingSubContracts.length > 1 ? "s" : ""} awaiting your validation</h2>
            </div>
            <Link to="/approvals" className="text-xs font-semibold text-[#1E3A8A] hover:underline" data-testid="dash-subcontracts-review">Review →</Link>
          </div>
        </div>
      )}

      {/* Hero finance band */}
      {!isStaff && (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 stagger">
        <div data-testid="kpi-total-income" className="relative overflow-hidden rounded-2xl border border-emerald-200/60 bg-gradient-to-br from-emerald-50 to-white p-6 shadow-premium card-hover">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700/70">Total Income</div>
            <div className="h-10 w-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center shadow-sm"><TrendingUp className="h-5 w-5" /></div>
          </div>
          <div className="mt-3 text-3xl font-extrabold font-mono-num tracking-tight text-emerald-700 truncate">{stats ? fmtFCFA(stats.total_income) : "—"}</div>
        </div>
        <div data-testid="kpi-total-expenditure" className="relative overflow-hidden rounded-2xl border border-slate-200/70 bg-gradient-to-br from-slate-50 to-white p-6 shadow-premium card-hover">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Total Expenditure</div>
            <div className="h-10 w-10 rounded-xl bg-slate-700 text-white flex items-center justify-center shadow-sm"><Receipt className="h-5 w-5" /></div>
          </div>
          <div className="mt-3 text-3xl font-extrabold font-mono-num tracking-tight text-slate-900 truncate">{stats ? fmtFCFA(stats.total_outflow) : "—"}</div>
        </div>
        <div data-testid="kpi-net-balance" className={`relative overflow-hidden rounded-2xl border p-6 shadow-premium card-hover ${stats && stats.net_balance < 0 ? "border-red-200/70 bg-gradient-to-br from-red-50 to-white" : "border-sky-200/60 bg-gradient-to-br from-sky-50 to-white"}`}>
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Net Balance</div>
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#1E3A8A] to-[#0284C7] text-white flex items-center justify-center shadow-sm"><Scale className="h-5 w-5" /></div>
          </div>
          <div className={`mt-3 text-3xl font-extrabold font-mono-num tracking-tight truncate ${stats && stats.net_balance < 0 ? "text-red-600" : "text-[#1E3A8A]"}`}>{stats ? fmtFCFA(stats.net_balance) : "—"}</div>
        </div>
      </div>
      )}

      {/* Secondary KPI grid */}
      {!isStaff && (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mt-4 stagger">
        <KpiCard title="Total Projects" icon={Briefcase} testId="kpi-total-projects">
          {stats?.total_projects ?? "—"}
        </KpiCard>
        <KpiCard title="Active" icon={Clock} testId="kpi-active-projects" accent="text-blue-700">
          {stats?.active_projects ?? "—"}
        </KpiCard>
        <KpiCard title="Completed" icon={CheckCircle2} testId="kpi-completed-projects" accent="text-emerald-700">
          {stats?.completed_projects ?? "—"}
        </KpiCard>
        <KpiCard title="Pending" icon={Clock} testId="kpi-pending-projects" accent="text-amber-600">
          {stats?.pending_projects ?? "—"}
        </KpiCard>
        <KpiCard title="Employees" icon={Users} testId="kpi-total-employees">
          {stats?.total_employees ?? "—"}
        </KpiCard>
      </div>
      )}

      {checkStats && checkStats.totals.issued_count > 0 && (
        <div className="mt-6" data-testid="dash-checks-summary">
          <Link to="/checks" className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-700 hover:text-[#1E3A8A]">
            <FileCheck className="h-4 w-4" /> Payment Checks
          </Link>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <KpiCard title={`Issued · ${checkStats.totals.issued_count} check(s)`} icon={FileCheck} testId="kpi-checks-issued">
              {fmtFCFA(checkStats.totals.issued_value)}
            </KpiCard>
            <KpiCard title="Pending / In Process" icon={Clock} accent="text-amber-600" testId="kpi-checks-pending">
              {fmtFCFA(checkStats.totals.pending_value)}
            </KpiCard>
            <KpiCard title="Paid" icon={CheckCircle2} accent="text-emerald-700" testId="kpi-checks-paid">
              {fmtFCFA(checkStats.totals.paid_value)}
            </KpiCard>
          </div>
        </div>
      )}

      {canSolar && solarSizings.length > 0 && (
        <div className="mt-6" data-testid="dash-solar-sizings">
          <Link to="/solar-calculator" className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-700 hover:text-[#1E3A8A]">
            <Sun className="h-4 w-4 text-amber-500" /> Recent solar sizings
          </Link>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {solarSizings.slice(0, 3).map((s) => (
              <Link key={s.id} to="/solar-calculator" data-testid={`dash-solar-${s.id}`} className="bg-white p-4 rounded-xl border border-slate-200/70 shadow-premium hover:shadow-md transition-shadow block">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold text-slate-800 truncate">{s.name}</div>
                  <Sun className="h-4 w-4 text-amber-500 shrink-0" />
                </div>
                {s.project_name && <div className="text-xs text-slate-400 truncate mt-0.5">{s.project_name}</div>}
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                  <span><span className="font-mono-num font-semibold text-slate-700">{(s.results?.num_panels ?? 0)}</span> panels</span>
                  <span><span className="font-mono-num font-semibold text-slate-700">{(s.results?.num_batteries ?? 0)}</span> batteries</span>
                  <span><span className="font-mono-num font-semibold text-slate-700">{Math.round(s.daily_wh || 0).toLocaleString()}</span> Wh/day</span>
                </div>
                <div className="text-xs text-slate-400 mt-2">{(s.created_at || "").slice(0, 10)}</div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Second row: outstanding + receivables + pending users */}
      {!isStaff && (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
        <div className="bg-white p-5 rounded-xl border border-slate-200/70 shadow-premium flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-red-50 border border-red-100 flex items-center justify-center text-red-500">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-400">Outstanding Balances</div>
              <div className="text-sm text-slate-500">Total project cash shortfalls</div>
            </div>
          </div>
          <div className="text-xl font-extrabold font-mono-num text-red-600">
            {stats ? fmtFCFA(stats.outstanding_balance) : "—"}
          </div>
        </div>
        {[ROLES.CEO, ROLES.FM, ROLES.ACEO].includes(user.role) && (
          <Link to="/projects" className="bg-white p-5 rounded-xl border border-slate-200/70 shadow-premium flex items-center justify-between hover:shadow-md transition-shadow" data-testid="dash-outstanding-receivables">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-amber-50 border border-amber-100 flex items-center justify-center text-amber-600">
                <Scale className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-400">Outstanding Receivables</div>
                <div className="text-sm text-slate-500">Unpaid contract balances (active projects)</div>
              </div>
            </div>
            <div className="text-xl font-extrabold font-mono-num text-amber-600" data-testid="dash-receivables-value">
              {stats ? fmtFCFA(stats.outstanding_receivables) : "—"}
            </div>
          </Link>
        )}
        {user.role === ROLES.CEO && (
          <Link to="/users" className="bg-white p-5 rounded-xl border border-slate-200/70 shadow-premium flex items-center justify-between hover:shadow-md transition-shadow" data-testid="dash-pending-users">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-sky-50 border border-sky-100 flex items-center justify-center text-sky-600">
                <UserPlus className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-400">Pending Approvals</div>
                <div className="text-sm text-slate-500">Users awaiting your approval</div>
              </div>
            </div>
            <div className="text-xl font-extrabold font-mono-num text-sky-600">{stats?.pending_users ?? "—"}</div>
          </Link>
        )}
      </div>
      )}

      {/* Quotation win-rate */}
      {canQuotes && quoteStats && quoteStats.total > 0 && (
        <div className="mt-6" data-testid="dash-quote-winrate">
          <Link to="/quotations" className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-700 hover:text-[#1E3A8A]">
            <FileSignature className="h-4 w-4" /> Quotations
          </Link>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div data-testid="kpi-quote-winrate" className="relative overflow-hidden rounded-2xl border border-emerald-200/60 bg-gradient-to-br from-emerald-50 to-white p-6 shadow-premium card-hover">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700/70">Win Rate</div>
              <div className="mt-2 text-3xl font-extrabold font-mono-num tracking-tight text-emerald-700">{quoteStats.win_rate}%</div>
              <div className="mt-1 text-xs text-slate-500">Accepted of decided quotes</div>
            </div>
            <KpiCard title="Sent" icon={Mail} testId="kpi-quote-sent" accent="text-blue-700">{quoteStats.sent}</KpiCard>
            <KpiCard title="Accepted" icon={CheckCircle2} testId="kpi-quote-accepted" accent="text-emerald-700">{quoteStats.accepted}</KpiCard>
            <KpiCard title="Converted" icon={Briefcase} testId="kpi-quote-converted" accent="text-[#1E3A8A]">{quoteStats.converted}</KpiCard>
          </div>
        </div>
      )}

      {/* Charts */}
      {!isStaff && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <CardShell id="income-exp" title="Income vs Expenditure (monthly)" note="· click a month for details" canToggle={canToggle} open={sections["income-exp"]} onToggle={() => setSection("income-exp", !sections["income-exp"])} className="lg:col-span-2">
          <ChartBox height={280}>
            {(w) => (
              <AreaChart width={w} height={280} data={charts?.monthly || []} onClick={(e) => openDrill(e && e.activeLabel)} style={{ cursor: "pointer" }}>
                <defs>
                  <linearGradient id="inc" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#059669" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#059669" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="exp" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#DC2626" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#DC2626" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748b" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => fmtFCFA(v)} {...TOOLTIP_STYLE} />
                <Area type="monotone" dataKey="income" stroke="#059669" fill="url(#inc)" strokeWidth={2.5} name="Income" dot={false} activeDot={{ r: 4, strokeWidth: 2 }} />
                <Area type="monotone" dataKey="expenditure" stroke="#DC2626" fill="url(#exp)" strokeWidth={2.5} name="Expenditure" dot={false} activeDot={{ r: 4, strokeWidth: 2 }} />
              </AreaChart>
            )}
          </ChartBox>
        </CardShell>
        <CardShell id="projects-category" title="Projects by Category" canToggle={canToggle} open={sections["projects-category"]} onToggle={() => setSection("projects-category", !sections["projects-category"])}>
          <ChartBox height={280}>
            {(w) => (
              <PieChart width={w} height={280}>
                <Pie data={charts?.category_breakdown || []} dataKey="value" nameKey="name"
                  cx="50%" cy="45%" outerRadius={80} innerRadius={45} paddingAngle={2}>
                  {(charts?.category_breakdown || []).map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            )}
          </ChartBox>
        </CardShell>
      </div>
      )}

      {/* Income by source (this month) */}
      {!isStaff && (
      <CardShell id="income-by-source" testId="dash-income-by-source" title="Income by Source" note={`· ${charts?.current_month || "this month"}`} canToggle={canToggle} open={sections["income-by-source"]} onToggle={() => setSection("income-by-source", !sections["income-by-source"])} className="mt-4">
        <ChartBox height={280}>
          {(w) => ((charts?.income_by_source || []).length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-slate-400" data-testid="income-source-empty">No income recorded this month.</div>
          ) : (
              <BarChart width={w} height={280} data={charts.income_by_source} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748b" }} interval={0} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => fmtFCFA(v)} {...TOOLTIP_STYLE} />
                <Bar dataKey="value" name="Income" radius={[6, 6, 0, 0]}>
                  {charts.income_by_source.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Bar>
              </BarChart>
          ))}
        </ChartBox>
      </CardShell>
      )}

      {/* Income by source trend (last 6 months) */}
      {!isStaff && (
      <CardShell id="income-source-trend" testId="dash-income-source-trend" title="Income by Source Trend" note="· month over month (last 6 months)" canToggle={canToggle} open={sections["income-source-trend"]} onToggle={() => setSection("income-source-trend", !sections["income-source-trend"])} className="mt-4">
        <ChartBox height={300}>
          {(w) => ((charts?.income_source_trend || []).length === 0 || (charts?.income_source_names || []).length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-slate-400" data-testid="income-source-trend-empty">No income recorded yet.</div>
          ) : (
              <BarChart width={w} height={300} data={charts.income_source_trend} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748b" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => fmtFCFA(v)} {...TOOLTIP_STYLE} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {(charts.income_source_names || []).map((s, i) => (
                  <Bar key={s} dataKey={s} stackId="src" name={s} fill={PIE_COLORS[i % PIE_COLORS.length]}
                    radius={i === (charts.income_source_names.length - 1) ? [6, 6, 0, 0] : [0, 0, 0, 0]} />
                ))}
              </BarChart>
          ))}
        </ChartBox>
      </CardShell>
      )}

      {/* Payments by purpose (this month) */}
      {[ROLES.CEO, ROLES.FM].includes(user.role) && (
      <CardShell id="payments-by-purpose" testId="dash-payments-by-purpose" title="Payments by Purpose" note={`· ${charts?.current_month || "this month"}`} canToggle={canToggle} open={sections["payments-by-purpose"]} onToggle={() => setSection("payments-by-purpose", !sections["payments-by-purpose"])} className="mt-4">
        <ChartBox height={280}>
          {(w) => ((charts?.payments_by_purpose || []).length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-slate-400" data-testid="payments-purpose-empty">No payments recorded this month.</div>
          ) : (
              <BarChart width={w} height={280} data={charts.payments_by_purpose} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748b" }} interval={0} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => fmtFCFA(v)} {...TOOLTIP_STYLE} />
                <Bar dataKey="value" name="Payments" radius={[6, 6, 0, 0]}>
                  {charts.payments_by_purpose.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Bar>
              </BarChart>
          ))}
        </ChartBox>
      </CardShell>
      )}

      {/* Expenditures by purpose (this month) */}
      {[ROLES.CEO, ROLES.FM].includes(user.role) && (
      <CardShell id="expenditures-by-purpose" testId="dash-expenditures-by-purpose" title="Expenditure by Purpose" note={`· ${charts?.current_month || "this month"}`} canToggle={canToggle} open={sections["expenditures-by-purpose"]} onToggle={() => setSection("expenditures-by-purpose", !sections["expenditures-by-purpose"])} className="mt-4">
        <ChartBox height={280}>
          {(w) => ((charts?.expenditures_by_purpose || []).length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-slate-400" data-testid="expenditures-purpose-empty">No expenditures recorded this month.</div>
          ) : (
              <BarChart width={w} height={280} data={charts.expenditures_by_purpose} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748b" }} interval={0} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => fmtFCFA(v)} {...TOOLTIP_STYLE} />
                <Bar dataKey="value" name="Expenditure" radius={[6, 6, 0, 0]}>
                  {charts.expenditures_by_purpose.map((_, i) => <Cell key={i} fill={["#DC2626", "#F59E0B", "#7C3AED", "#0284C7", "#059669", "#64748B", "#1E3A8A"][i % 7]} />)}
                </Bar>
              </BarChart>
          ))}
        </ChartBox>
      </CardShell>
      )}

      {/* Recent projects */}
      {!isStaff && (
      <div className="bg-white rounded-xl border border-slate-200/70 shadow-premium mt-6 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-heading font-semibold text-slate-800">Recent Projects</h3>
          <Link to="/projects" className="text-sm text-blue-700 hover:underline">View all</Link>
        </div>
        <div className="divide-y divide-slate-100">
          {(recent?.projects || []).length === 0 && (
            <div className="px-5 py-8 text-center text-slate-400 text-sm">No projects yet.</div>
          )}
          {(recent?.projects || []).map((p) => (
            <Link to={`/projects/${p.id}`} key={p.id} className="flex items-center justify-between px-5 py-3 hover:bg-slate-50">
              <div>
                <div className="font-medium text-slate-800">{p.name}</div>
                <div className="text-xs text-slate-400">{p.category} · {p.owner}</div>
              </div>
              <div className="flex items-center gap-3">
                {!isStaff && <span className="text-sm font-mono-num text-slate-600">{fmtFCFA(p.total_cost)}</span>}
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${statusBadgeClass(p.status)}`}>{p.status}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
      )}

            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Dialog open={!!drill} onOpenChange={(v) => !v && setDrill(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Transactions — {drill?.month}</DialogTitle>
            <DialogDescription>Income, expenditure and payments recorded in this month.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 mb-2">
            <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
              <div className="text-xs text-slate-500">Income</div>
              <div className="font-mono-num font-bold text-emerald-700">{fmtFCFA(drill?.data?.total_income)}</div>
            </div>
            <div className="bg-red-50 border border-red-100 rounded-lg p-3">
              <div className="text-xs text-slate-500">Expenditure + Payments</div>
              <div className="font-mono-num font-bold text-red-600">{fmtFCFA(drill?.data?.total_expenditure)}</div>
            </div>
          </div>
          <DrillList title="Income" items={drill?.data?.income} color="text-emerald-700" label={(x) => x.project_name || "—"} />
          <DrillList title="Expenditure" items={drill?.data?.expenditures} color="text-slate-800" label={(x) => x.purpose} />
          <DrillList title="Payments" items={drill?.data?.payments} color="text-slate-800" label={(x) => `${x.purpose}${x.employee_name ? ` · ${x.employee_name}` : ""}`} />
        </DialogContent>
      </Dialog>

      <Dialog open={phoneOpen} onOpenChange={(v) => { if (!v) setPhoneOpen(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit phone number</DialogTitle>
            <DialogDescription>Update the phone number shown on your profile.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <PhoneField value={phoneVal} onChange={(v) => { setPhoneVal(v); setPhoneErr(false); }} invalid={phoneErr} testId="edit-phone-field" />
            {phoneErr && <div className="text-xs text-red-600" data-testid="edit-phone-error">Enter a valid phone number in international format, e.g. +237677123456.</div>}
            <div className="flex justify-end gap-2 pt-1">
              <button data-testid="edit-phone-cancel" onClick={() => setPhoneOpen(false)} className="h-9 px-4 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50">Cancel</button>
              <button data-testid="edit-phone-save" onClick={savePhone} className="h-9 px-4 rounded-lg bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#152C6F]">Save</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProfileField({ label, value }) {
  return (
    <div data-testid={`profile-field-${label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}>
      <div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-sm text-slate-700 font-medium mt-0.5 break-words">{value || "—"}</div>
    </div>
  );
}

function CardShell({ id, title, note, canToggle, testId, className = "", open, onToggle, children }) {
  return (
    <div data-testid={testId} className={`bg-white p-5 rounded-xl border border-slate-200/70 shadow-premium ${className}`}>
      <div className={`flex items-center justify-between gap-2 ${open ? "mb-4" : ""}`}>
        <h3 className="font-heading font-semibold text-slate-800">{title}{note && <span className="text-xs font-normal text-slate-400"> {note}</span>}</h3>
        {canToggle && (
          <motion.button data-testid={`section-toggle-${id}`} onClick={onToggle}
            whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.9 }}
            className="shrink-0 h-7 w-7 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 flex items-center justify-center"
            aria-label={open ? "Collapse section" : "Expand section"} aria-pressed={!open}>
            <motion.span animate={{ rotate: open ? 0 : -90 }} transition={{ type: "spring", stiffness: 300, damping: 20 }} className="flex">
              <ChevronDown className="h-4 w-4" />
            </motion.span>
          </motion.button>
        )}
      </div>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div key="body" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} style={{ overflow: "hidden" }}>
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function DrillList({ title, items, color, label }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-3">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">{title}</div>
      <div className="divide-y divide-slate-100 border border-slate-100 rounded-lg">
        {items.map((x, i) => (
          <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
            <span className="text-slate-600">{fmtDate(x.date || x.created_at)} · {label(x)}</span>
            <span className={`font-mono-num font-medium ${color}`}>{fmtFCFA(x.amount)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChartBox({ height = 280, children, className = "" }) {
  const ref = useRef(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const cw = entries[0]?.contentRect?.width || 0;
      if (cw > 0) setW(cw);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return <div ref={ref} className={`w-full ${className}`} style={{ height }}>{w > 0 ? children(w) : null}</div>;
}
